/**
 * Generic pagination utilities for mock data
 * Calculates totalPages, hasNextPage, hasPreviousPage and slices data
 */

export interface PaginationParams {
  /** Current page number (1-indexed) */
  page?: number
  /** Items per page */
  limit?: number
}

export interface PaginationMeta {
  /** Total number of items */
  total: number
  /** Current page number (1-indexed) */
  page: number
  /** Items per page */
  limit: number
  /** Total number of pages */
  totalPages: number
  /** Whether there is a next page */
  hasNext: boolean
  /** Whether there is a previous page */
  hasPrev: boolean
  /** First item index on current page (0-indexed) */
  startIndex: number
  /** Last item index on current page (0-indexed, exclusive) */
  endIndex: number
}

export interface PaginatedResult<T> {
  /** Paginated items for current page */
  items: T[]
  /** Pagination metadata */
  pagination: PaginationMeta
}

/**
 * Default pagination values
 */
export const PAGINATION_DEFAULTS = {
  page: 1,
  limit: 25
} as const

/**
 * Allowed limit values (to prevent abuse)
 */
export const ALLOWED_LIMITS = [10, 25, 50, 100] as const
export type AllowedLimit = (typeof ALLOWED_LIMITS)[number]

/**
 * Validate and normalize pagination parameters
 */
export function normalizePaginationParams(params: PaginationParams): Required<PaginationParams> {
  let { page = PAGINATION_DEFAULTS.page, limit = PAGINATION_DEFAULTS.limit } = params

  // Ensure page is at least 1
  page = Math.max(1, Math.floor(page))

  // Ensure limit is valid
  if (!ALLOWED_LIMITS.includes(limit as AllowedLimit)) {
    // Find the closest allowed limit
    limit = ALLOWED_LIMITS.reduce((prev, curr) =>
      Math.abs(curr - limit!) < Math.abs(prev - limit!) ? curr : prev
    )
  }

  return { page, limit }
}

/**
 * Calculate pagination metadata
 */
export function calculatePaginationMeta(
  totalItems: number,
  page: number,
  limit: number
): PaginationMeta {
  const totalPages = Math.max(1, Math.ceil(totalItems / limit))

  // Clamp page to valid range
  const clampedPage = Math.min(Math.max(1, page), totalPages)

  const startIndex = (clampedPage - 1) * limit
  const endIndex = Math.min(startIndex + limit, totalItems)

  return {
    total: totalItems,
    page: clampedPage,
    limit,
    totalPages,
    hasNext: clampedPage < totalPages,
    hasPrev: clampedPage > 1,
    startIndex,
    endIndex
  }
}

/**
 * Apply pagination to a dataset
 *
 * @example
 * ```ts
 * const result = applyPagination(transactions, { page: 2, limit: 25 })
 * // result.items - transactions for page 2
 * // result.pagination - { total, page, limit, totalPages, hasNext, hasPrev, ... }
 * ```
 */
export function applyPagination<T>(
  data: T[],
  params: PaginationParams = {}
): PaginatedResult<T> {
  const { page, limit } = normalizePaginationParams(params)
  const meta = calculatePaginationMeta(data.length, page, limit)

  const items = data.slice(meta.startIndex, meta.endIndex)

  return {
    items,
    pagination: meta
  }
}

/**
 * Parse pagination parameters from URL search params
 */
export function parsePaginationParams(searchParams: URLSearchParams): PaginationParams {
  const pageStr = searchParams.get('page')
  const limitStr = searchParams.get('limit')

  return {
    page: pageStr ? Number.parseInt(pageStr, 10) : undefined,
    limit: limitStr ? Number.parseInt(limitStr, 10) : undefined
  }
}

/**
 * Build a response object with pagination for API responses
 */
export function buildPaginatedResponse<T, K extends string>(
  items: T[],
  params: PaginationParams,
  dataKey: K
): { [key in K]: T[] } & { pagination: PaginationMeta } {
  const result = applyPagination(items, params)

  return {
    [dataKey]: result.items,
    pagination: result.pagination
  } as { [key in K]: T[] } & { pagination: PaginationMeta }
}

/**
 * Get page info summary for logging
 */
export function getPageInfoSummary(meta: PaginationMeta): string {
  return `Page ${meta.page}/${meta.totalPages} (${meta.total} total, showing ${meta.endIndex - meta.startIndex})`
}
